import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, DownloadSimple, Lock, CheckCircle, Palette, ArrowSquareOut, Eye, Monitor, Copy } from '@phosphor-icons/react';
import FlipBook from './FlipBook';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONT_OPTIONS = [
  { id: 'serif',  label: 'Classic',      css: '"Playfair Display", Georgia, serif' },
  { id: 'script', label: 'Handwritten',  css: '"Dancing Script", "Brush Script MT", cursive' },
  { id: 'sans',   label: 'Modern',       css: '"Inter", system-ui, sans-serif' },
  { id: 'modern', label: 'Bold',         css: '"Montserrat", "Inter", sans-serif' },
];

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Host Memory Book module — Stripe-gated A5 PDF generator.
 * Sits alongside Film/Highlight/Slideshow modules on the event dashboard.
 */
const MemoryBookModule = ({ event }) => {
  const eventId = event?.event_id;
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [purchased, setPurchased] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [entryCount, setEntryCount] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [latestBookAt, setLatestBookAt] = useState(event?.memory_book_latest_at || null);
  const [saving, setSaving] = useState(false);
  const [flipOpen, setFlipOpen] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [s, entries, tpls] = await Promise.all([
          axios.get(`${API}/events/${eventId}/memory-book/settings`, auth()).then(r => r.data).catch(() => null),
          axios.get(`${API}/events/${eventId}/guestbook/entries`).then(r => r.data).catch(() => ({ count: 0 })),
          axios.get(`${API}/template-library`).then(r => r.data).catch(() => []),
        ]);
        if (cancelled) return;
        if (s) {
          setSettings(s.settings || {});
          setPurchased(!!(s.memory_book_purchased || s.has_pro_upgrade));
        }
        setEntryCount(entries?.count || 0);
        setTemplates(Array.isArray(tpls) ? tpls : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [eventId]);

  const updateSetting = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/events/${eventId}/memory-book/settings`, settings, auth());
      toast.success('Memory Guest Book settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const startPurchase = async () => {
    try {
      const res = await axios.post(`${API}/events/${eventId}/memory-book/purchase`, {}, auth());
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      // Save latest settings first so the PDF picks them up
      await axios.put(`${API}/events/${eventId}/memory-book/settings`, settings || {}, auth()).catch(() => {});
      const res = await axios.post(`${API}/events/${eventId}/memory-book/generate`, {}, auth());
      setLatestBookAt(res.data?.created_at || new Date().toISOString());
      toast.success('Memory Guest Book PDF ready — download below');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/memory-book/download`, {
        ...auth(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (event?.event_name || 'event').replace(/\s+/g, '-').toLowerCase();
      a.download = `eventdrop-memory-book-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  if (loading || !settings) {
    return (
      <div className="rounded-2xl p-6 border border-white/10 bg-[#15161B] flex items-center gap-3" data-testid="memory-book-loading">
        <div className="w-4 h-4 border-2 border-[#B89762]/45 border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-400 text-sm">Loading Memory Guest Book…</span>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-6 border relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(184,151,98,0.10), rgba(194,157,82,0.06))',
        borderColor: 'rgba(184,151,98,0.25)',
      }}
      data-testid="memory-book-module"
    >
      <div className="flex items-start gap-4 mb-5">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        >
          <BookOpen size={22} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-white text-lg">Printed Memory Guest Book</h4>
            {purchased ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">UNLOCKED</span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/70">£29 add-on</span>
            )}
          </div>
          <p className="text-sm text-gray-300 mt-0.5">
            A5 keepsake PDF — cover, guest messages, photo gallery. Arrange printing anywhere.
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl p-3 bg-white/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Guest messages</div>
          <div className="text-xl font-black text-white mt-1" data-testid="mb-entry-count">{entryCount}</div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <a
              href={`/event/${event.slug}/memory-book-message`}
              target="_blank" rel="noreferrer"
              className="text-[10px] text-[#C29D52] hover:text-[#C29D52]/85 inline-flex items-center gap-1"
              data-testid="mb-guest-link"
            >
              Share guest link <ArrowSquareOut size={10} weight="bold" />
            </a>
            {entryCount > 0 && (
              <button
                onClick={() => setFlipOpen(true)}
                className="text-[10px] text-[#C29D52] hover:text-[#C29D52]/85 inline-flex items-center gap-1"
                data-testid="mb-view-flipbook"
              >
                <Eye size={10} weight="bold" /> View flipbook
              </button>
            )}
          </div>
        </div>
        <div className="rounded-xl p-3 bg-white/5 border border-white/10">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Latest PDF</div>
          <div className="text-xl font-black text-white mt-1" data-testid="mb-latest-at">
            {latestBookAt ? new Date(latestBookAt).toLocaleDateString('en-GB') : '—'}
          </div>
          <div className="text-[10px] text-gray-500 mt-1">{latestBookAt ? 'Regenerate any time' : 'Not generated yet'}</div>
        </div>
      </div>

      {/* Cover editor */}
      <div className="space-y-3 mb-5">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Cover title</span>
          <input
            type="text"
            value={settings.cover_title || ''}
            onChange={(e) => updateSetting('cover_title', e.target.value)}
            maxLength={80}
            className="mt-1 w-full h-10 rounded-lg bg-[#0F1014] border border-white/10 px-3 text-sm text-white"
            data-testid="mb-cover-title"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Subtitle</span>
            <input
              type="text"
              value={settings.cover_subtitle || ''}
              onChange={(e) => updateSetting('cover_subtitle', e.target.value)}
              maxLength={80}
              className="mt-1 w-full h-10 rounded-lg bg-[#0F1014] border border-white/10 px-3 text-sm text-white"
              data-testid="mb-cover-subtitle"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Cover date</span>
            <input
              type="text"
              value={settings.cover_date || ''}
              onChange={(e) => updateSetting('cover_date', e.target.value)}
              maxLength={40}
              placeholder="e.g. 12 August 2026"
              className="mt-1 w-full h-10 rounded-lg bg-[#0F1014] border border-white/10 px-3 text-sm text-white"
              data-testid="mb-cover-date"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Dedication (first page)</span>
          <textarea
            value={settings.dedication || ''}
            onChange={(e) => updateSetting('dedication', e.target.value)}
            maxLength={300}
            rows={2}
            className="mt-1 w-full rounded-lg bg-[#0F1014] border border-white/10 px-3 py-2 text-sm text-white resize-y"
            data-testid="mb-dedication"
          />
        </label>

        <div>
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Font style</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {FONT_OPTIONS.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => updateSetting('font_family', f.id)}
                className={`px-3 h-9 rounded-lg text-xs font-semibold transition-all border ${
                  settings.font_family === f.id ? 'border-white/40 bg-white/10 text-white' : 'border-white/10 bg-white/0 text-white/60 hover:text-white'
                }`}
                style={{ fontFamily: f.css }}
                data-testid={`mb-setting-font-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Template picker (shared with PocketBooth library) */}
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Palette size={12} weight="bold" className="text-gray-400" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Cover background</span>
            {settings.template_id && (
              <button
                onClick={() => updateSetting('template_id', null)}
                className="ml-auto text-[10px] text-red-400 hover:text-red-300"
                data-testid="mb-clear-template"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-white/40 text-[10px] mb-2">Matches your Pocket Booth — same template library</p>
          {templates.length === 0 ? (
            <div className="text-white/40 text-xs">No templates yet — ask your admin to upload cover art.</div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {templates.map(t => (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => updateSetting('template_id', t.template_id)}
                  className={`flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden border-2 transition-all relative ${
                    settings.template_id === t.template_id ? 'border-white scale-[1.04]' : 'border-white/10 hover:border-white/30'
                  }`}
                  data-testid={`mb-template-${t.template_id}`}
                >
                  <img
                    src={`${API}/template-library/${t.template_id}/image`}
                    alt={t.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {settings.template_id === t.template_id && (
                    <div className="absolute top-1 right-1 bg-white text-[#B89762] rounded-full p-0.5">
                      <CheckCircle size={14} weight="fill" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/70">
                    <p className="text-white text-[8px] font-semibold truncate">{t.name}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Save + gated CTA */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={saveSettings}
          disabled={saving}
          className="inline-flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors disabled:opacity-50"
          data-testid="mb-save-settings"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>

        {!purchased ? (
          <button
            type="button"
            onClick={startPurchase}
            className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-bold text-white transition-all active:scale-[0.97]"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="mb-unlock"
          >
            <Lock size={14} weight="bold" /> Unlock Memory Guest Book · £29
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-bold text-white transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="mb-generate"
            >
              {generating ? 'Generating PDF…' : 'Generate PDF'}
            </button>
            {latestBookAt && (
              <button
                type="button"
                onClick={downloadPdf}
                className="inline-flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
                data-testid="mb-download"
              >
                <DownloadSimple size={14} weight="bold" /> Download PDF
              </button>
            )}
          </>
        )}
      </div>

      <p className="text-[11px] text-gray-500 mt-3">
        Size: A5 (148×210 mm). Download the PDF and arrange printing with any service you like.
      </p>

      {/* Live Book URL — shareable on a venue TV or projector */}
      <div
        className="mt-4 rounded-xl border border-[#B89762]/50/20 bg-[#B89762]/5 p-3 flex items-center gap-3 flex-wrap"
        data-testid="mb-live-book-row"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #C29D52, #B89762)' }}
        >
          <Monitor size={16} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <div className="text-xs font-bold text-white">Live Book mode</div>
          <div className="text-[10px] text-gray-400 leading-snug">
            Full-screen flipbook with QR code — perfect for a venue TV or projector.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const url = `${window.location.origin}/event/${event.slug}/live-book`;
              navigator.clipboard?.writeText(url).then(
                () => toast.success('Live Book URL copied'),
                () => toast.error('Copy failed — long-press to copy manually'),
              );
            }}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[11px] font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
            data-testid="mb-live-book-copy"
          >
            <Copy size={11} weight="bold" /> Copy link
          </button>
          <a
            href={`/event/${event.slug}/live-book`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[11px] font-bold text-white transition-colors"
            style={{ background: 'linear-gradient(135deg, #C29D52, #B89762)' }}
            data-testid="mb-live-book-open"
          >
            Open <ArrowSquareOut size={11} weight="bold" />
          </a>
        </div>
      </div>

      {flipOpen && (
        <FlipBook slug={event.slug} onClose={() => setFlipOpen(false)} eventNameFallback={event.event_name} />
      )}
    </div>
  );
};

export default MemoryBookModule;
